import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// Feature icon components
const PackerIcon = () => (
  <svg className="w-8 h-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

const SlicerIcon = () => (
  <svg className="w-8 h-8 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879a3 3 0 11-4.242-4.242L10.5 5.5m0 0l-7 7m7-7v14" />
  </svg>
);

const AnimationIcon = () => (
  <svg className="w-8 h-8 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const EngineIcon = () => (
  <svg className="w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const SecurityIcon = () => (
  <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

// Feature card component
const FeatureCard = ({ 
  title, 
  description, 
  features, 
  icon,
  imageSrc,
  customIllustration,
  isReversed = false,
  bgColor = 'bg-white'
}: { 
  title: string, 
  description: string, 
  features: string[], 
  icon: React.ReactNode,
  imageSrc?: string,
  customIllustration?: React.ReactNode,
  isReversed?: boolean,
  bgColor?: string
}) => {
  return (
    <div className={`${bgColor} dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden mb-12 transition-all hover:shadow-xl`}>
      <div className={`flex flex-col ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'} items-center`}>
        <div className="md:w-1/2 p-8 lg:p-12 flex justify-center">
          <div className="w-full max-w-md flex flex-col items-center md:items-start">
            <div className="p-3 rounded-xl bg-orange-50 dark:bg-gray-700 shadow-sm mb-4">
              {icon}
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 text-center md:text-left">{title}</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6 text-center md:text-left leading-relaxed">{description}</p>
            <ul className="space-y-2.5 w-full">
              {(Array.isArray(features) ? features : []).map((feature, index) => (
                <li key={index} className="flex items-start">
                  <svg className="w-5 h-5 text-orange-500 mr-2.5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="md:w-1/2 bg-gray-950 p-6 sm:p-8 flex items-center justify-center w-full min-h-[320px]">
          {imageSrc ? (
            <div className="relative group overflow-hidden rounded-xl border border-gray-800 shadow-2xl">
              <img 
                src={imageSrc} 
                alt={title} 
                className="w-full h-auto max-h-[360px] object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          ) : customIllustration ? (
            customIllustration
          ) : null}
        </div>
      </div>
    </div>
  );
};

// 动画播放器视觉卡片插画
const AnimationIllustration = () => {
  return (
    <div className="w-full max-w-sm rounded-xl bg-gradient-to-br from-gray-900 to-slate-900 p-6 border border-gray-800 text-center flex flex-col items-center shadow-2xl">
      <div className="w-full flex items-center justify-between pb-3 border-b border-gray-800 mb-4 text-xs font-mono">
        <span className="text-orange-400 font-bold">ANIMATION PLAYER</span>
        <span className="text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-800/60">FPS: 24 (41.6ms)</span>
      </div>
      {/* 模拟动效帧展示容器 */}
      <div className="w-full h-32 rounded-lg bg-gray-950 border border-gray-800 flex items-center justify-center relative overflow-hidden mb-4">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)',
          backgroundSize: '12px 12px'
        }}></div>
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-12 h-14 border border-purple-500/40 rounded bg-purple-500/10 flex items-center justify-center text-xs text-purple-300 font-mono">Frame 1</div>
          <div className="w-14 h-16 border-2 border-orange-500 rounded bg-orange-500/20 flex flex-col items-center justify-center text-xs text-orange-200 font-bold font-mono shadow-lg shadow-orange-500/20 animate-pulse">
            <span>Frame 2</span>
            <span className="text-[9px] text-orange-400">ACTIVE</span>
          </div>
          <div className="w-12 h-14 border border-purple-500/40 rounded bg-purple-500/10 flex items-center justify-center text-xs text-purple-300 font-mono">Frame 3</div>
        </div>
      </div>
      {/* 控制条模拟 */}
      <div className="w-full flex items-center justify-between px-3 py-2 bg-gray-800/70 rounded-lg border border-gray-700 text-xs font-mono text-gray-300">
        <span className="text-orange-400">▶ LOOPING</span>
        <span className="text-gray-400">PingPong: ON</span>
        <span className="text-purple-300">6 Frames</span>
      </div>
    </div>
  );
};

// 离线安全自定义视觉插画
const LocalSecurityIllustration = () => {
  const { t } = useTranslation();
  return (
    <div className="w-full max-w-sm rounded-xl bg-gradient-to-br from-gray-900 to-slate-900 p-6 border border-gray-800 text-center flex flex-col items-center shadow-2xl">
      <div className="relative mb-4">
        <div className="w-20 h-20 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shadow-inner">
          <svg className="w-10 h-10 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <span className="absolute -bottom-1 -right-1 flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500"></span>
        </span>
      </div>
      <h4 className="text-lg font-bold text-white mb-1">
        {t('features.offlineIllustration.title', '100% Local Sandbox')}
      </h4>
      <p className="text-xs text-gray-400 mb-4">
        {t('features.offlineIllustration.desc', 'All algorithms run in local memory')}
      </p>
      <div className="w-full bg-gray-800/80 rounded-lg p-2.5 flex justify-around text-[11px] font-mono text-gray-300 border border-gray-700">
        <span className="text-emerald-400">{t('features.offlineIllustration.encrypted', '🔒 In-Memory Processing')}</span>
        <span>|</span>
        <span>{t('features.offlineIllustration.latency', '⚡ 0ms Network Latency')}</span>
      </div>
    </div>
  );
};

// 引擎与生态适配插画
const CrossPlatformIllustration = () => {
  return (
    <div className="w-full max-w-sm rounded-xl bg-gradient-to-br from-gray-900 to-slate-900 p-6 border border-gray-800 text-center flex flex-col items-center shadow-2xl">
      <div className="grid grid-cols-2 gap-3 w-full mb-4">
        <div className="bg-gray-800/60 p-3 rounded-lg border border-gray-700 flex flex-col items-center">
          <span className="text-orange-400 text-xl font-black">10+</span>
          <span className="text-[10px] text-gray-400 mt-1">Engines & Formats</span>
        </div>
        <div className="bg-gray-800/60 p-3 rounded-lg border border-gray-700 flex flex-col items-center">
          <span className="text-cyan-400 text-xl font-black">Template</span>
          <span className="text-[10px] text-gray-400 mt-1">Custom XML / JSON</span>
        </div>
      </div>
      <div className="w-full flex flex-wrap justify-center gap-2 text-xs font-semibold text-gray-300">
        <span className="bg-orange-500/20 text-orange-300 border border-orange-500/30 px-2.5 py-1 rounded">Unity</span>
        <span className="bg-blue-500/20 text-blue-300 border border-blue-500/30 px-2.5 py-1 rounded">Godot 4</span>
        <span className="bg-purple-500/20 text-purple-300 border border-purple-500/30 px-2.5 py-1 rounded">Cocos2d</span>
        <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2.5 py-1 rounded">Phaser 3</span>
        <span className="bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2.5 py-1 rounded">TexturePacker</span>
      </div>
    </div>
  );
};

const FeaturesPage: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="bg-slate-50 dark:bg-slate-900 py-12 md:py-20 min-h-screen">
      <div className="mx-auto max-w-6xl px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block py-1 px-3 mb-3 text-xs font-semibold uppercase tracking-wider text-orange-600 bg-orange-100 dark:bg-orange-950/60 dark:text-orange-400 rounded-full">
            {t('features.badge', 'AtlasSlicer 2D Capabilities')}
          </span>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-4xl md:text-5xl">
            {t('features.title')}
          </h1>
          <p className="mt-4 text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {t('features.subtitle')}
          </p>
          <p className="mt-4 text-base text-gray-500 dark:text-gray-400 max-w-3xl mx-auto">
            {t('features.description')}
          </p>
        </div>
        
        {/* Feature Cards */}
        <div className="mt-12 space-y-8">
          {/* Card 1: Rust 原生图集打包 */}
          <FeatureCard 
            title={t('features.packer.title')}
            description={t('features.packer.description')}
            features={t('features.packer.features', { returnObjects: true }) as string[]}
            icon={<PackerIcon />}
            imageSrc="/images/features/atlas_packer.png"
            bgColor="bg-white dark:bg-gray-800"
          />
          
          {/* Card 2: 4合1 智能切片 */}
          <FeatureCard 
            title={t('features.slicer.title')}
            description={t('features.slicer.description')}
            features={t('features.slicer.features', { returnObjects: true }) as string[]}
            icon={<SlicerIcon />}
            imageSrc="/images/features/smart_slicer.png"
            isReversed={true}
            bgColor="bg-white dark:bg-gray-800"
          />
          
          {/* Card 3: 实时动效质检播放器 */}
          <FeatureCard 
            title={t('features.animation.title')}
            description={t('features.animation.description')}
            features={t('features.animation.features', { returnObjects: true }) as string[]}
            icon={<AnimationIcon />}
            imageSrc="/images/features/animation_player.png"
            bgColor="bg-white dark:bg-gray-800"
          />
          
          {/* Card 4: 10大游戏引擎与自定义模板 */}
          <FeatureCard 
            title={t('features.engines.title')}
            description={t('features.engines.description')}
            features={t('features.engines.features', { returnObjects: true }) as string[]}
            icon={<EngineIcon />}
            customIllustration={<CrossPlatformIllustration />}
            isReversed={true}
            bgColor="bg-white dark:bg-gray-800"
          />

          {/* Card 5: 100% 纯本地离线安全与高性能 */}
          <FeatureCard 
            title={t('features.security.title')}
            description={t('features.security.description')}
            features={t('features.security.features', { returnObjects: true }) as string[]}
            icon={<SecurityIcon />}
            customIllustration={<LocalSecurityIllustration />}
            bgColor="bg-white dark:bg-gray-800"
          />
        </div>
        
        {/* Footer CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-orange-500/10 via-orange-500/5 to-transparent dark:from-orange-500/20 p-10 rounded-2xl border border-orange-500/20">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {t('features.content')}
          </h3>
          <p className="text-base text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-6">
            {t('features.ctaSubtitle', '14-day money-back guarantee. Pay once, use forever. Accelerate your game workflow now!')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/pricing" 
              className="w-full sm:w-auto bg-orange-500 text-white px-8 py-3.5 rounded-xl font-bold text-lg hover:bg-orange-600 shadow-lg hover:shadow-orange-500/30 transition-all transform active:scale-95"
            >
              {t('home.getStarted')}
            </Link>
            <Link 
              to="/download" 
              className="w-full sm:w-auto bg-white dark:bg-gray-800 text-gray-800 dark:text-white border border-gray-300 dark:border-gray-700 px-8 py-3.5 rounded-xl font-bold text-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
            >
              {t('home.downloadClient', '下载桌面客户端')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturesPage;
